// Heat semantics for the Constellations explorer — pure helpers so the legend,
// the bubble colors and the tooltips all read from ONE place (and are unit-
// testable without rendering).
//
// What "heat" IS: each cluster's temporal.heat is computed server-side
// (services/signals/cluster_time.py) from the EVENT date of its signals
// (metadata.observed_at — when the thing happened — falling back to the
// ingest date only when a signal carries no event date). It is NOT ingest
// recency and NOT cluster size. The tiers:
//   hot  — newest signal within `hot_days` AND at least `hot_min_recent`
//          dated signals (or `hot_share` of them) inside that window
//   warm — newest signal within `warm_days`
//   cold — nothing newer than `warm_days`
//   (undated — none of the sampled signals carries a usable date)
// Bubble COLOR encodes exactly this tier (kanban 1aef8153: the old palette
// colored bubbles by list index, which read as meaning but meant nothing).

import type { ClusterHeatRules, ClusterTemporal } from '../services/signalsClusters';

export type HeatTier = ClusterTemporal['heat'];
export type BubbleTone = HeatTier | 'undated' | 'outliers';

// Fallback only — the backend serves the live values on every payload.
export const DEFAULT_HEAT_RULES: ClusterHeatRules = {
  hot_days: 30,
  hot_min_recent: 2,
  hot_share: 0.5,
  warm_days: 90,
};

export const HEAT_COLORS: Record<BubbleTone, string> = {
  hot: '#e03131',
  warm: '#f08c00',
  cold: '#868e96',
  undated: '#5c7cfa',
  outliers: '#adb5bd',
};

export function bubbleTone(cluster: {
  temporal: ClusterTemporal | null;
  is_noise?: boolean;
}): BubbleTone {
  if (cluster.is_noise) return 'outliers';
  return cluster.temporal?.heat ?? 'undated';
}

export interface HeatLegendRow {
  tone: BubbleTone;
  label: string;
  meaning: string;
}

// The legend rows, in the order they read best (most to least recent).
export function heatLegend(rules: ClusterHeatRules = DEFAULT_HEAT_RULES): HeatLegendRow[] {
  const share = Math.round(rules.hot_share * 100);
  return [
    {
      tone: 'hot',
      label: 'Hot',
      meaning: `new signals in the last ${rules.hot_days} days (at least ${rules.hot_min_recent}, or ${share}% of the cluster)`,
    },
    {
      tone: 'warm',
      label: 'Warm',
      meaning: `latest signal within ${rules.warm_days} days`,
    },
    {
      tone: 'cold',
      label: 'Cold',
      meaning: `nothing newer than ${rules.warm_days} days`,
    },
    {
      tone: 'undated',
      label: 'Undated',
      meaning: 'signals carry no event date',
    },
  ];
}

function fmtDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

// One line a first-time reader can act on: the tier, the basis, the dates.
export function describeHeat(
  temporal: ClusterTemporal | null,
  rules: ClusterHeatRules = DEFAULT_HEAT_RULES,
): string {
  if (!temporal) return 'Undated — none of these signals carries an event date';
  const latest = fmtDay(temporal.last_seen);
  const basis = `${temporal.recent_count} of ${temporal.dated_count} dated signals in the last ${rules.hot_days} days`;
  switch (temporal.heat) {
    case 'hot':
      return `Hot — ${basis}; latest ${latest}`;
    case 'warm':
      return `Warm — latest signal ${latest} (within ${rules.warm_days} days); ${basis}`;
    default:
      return `Cold — latest signal ${latest} (over ${rules.warm_days} days ago)`;
  }
}
